// IE 2026 10 06 / ODOO-341407 (заказ Игоря п.1 / п.2: поля переводов в UI): чистые помощники без Vue — тестируются в node (tests/unit/i18nFields.test.mjs).
// Дизайн — ie-webscm7-2 docs/i18n_fields_ui_2026-10-06/DESIGN.md §1, §3. Что здесь:
//   - какое поле переводимое (isTranslatable) и где лежит его перевод (fieldPath / localizeBinding — то же правило, что store.localize_binding:
//     title записи → meta.title_<L>, остальное → <binding>_<L>, поле строки конфигурации (nested) → <binding>_<L> рядом с ключом). Путь
//     ВЫВОДИТСЯ из binding поля (meta.comment → meta.comment_uk, doc.x → doc.x_uk), контейнеры doc. / meta. не зашиты (требование ie-dm7, Odoo 5800);
//   - описания виртуальных строк формы «<поле> (uk)/(ru)» (formRowsFor) и колонок списка (listColsFor) — контракты DESIGN §3.2 и §3.4;
//   - словарный перевод предопределённой записи (meta._i18n_auto, бэк attrs.localize_record_meta): снятие пометки при правке (autoUnmark)
//     и копия строки без неправленых словарных meta.title_<L> для сохранения списка (stripAutoForSave) — строка в памяти не меняется;
//   - личная опция «Show translation fields» (решение Игоря В1): состояние по СЕГМЕНТУ ДАННЫХ (одно у списка и у форм его записей), рабочее —
//     в реактивной карте стора store.i18nFields = { key, map }, запоминание — localStorage 'i18nFields:<user>:<db>' (каждое обращение в
//     try/catch; хранилище недоступно — работает из памяти). Запись виджета и вид НЕ трогаются: ни _meta.modified, ни doc.viewParams.
// Выключатели (реестр feSettings): fe.i18n.translationFields — весь механизм (по умолчанию вкл.; сама опция у пользователя по умолчанию выкл.),
// fe.i18n.formEditTranslation — правка основной translate-строки формы под uk/ru пишет только перевод (по умолчанию вкл., решение Игоря В3).
import { t } from '@/i18n'

export const FLAG_FIELDS = 'fe.i18n.translationFields'
export const FLAG_FORM_EDIT = 'fe.i18n.formEditTranslation'
export const AUTO_MARK = '_i18n_auto'          // meta._i18n_auto: ['title_uk'] — словарный перевод в копии ответа (бэк attrs.py localize_record_meta, фронт relocalize.js)
export const STORAGE_PREFIX = 'i18nFields'
// UI-строки (ключи словаря; uk / ru — src/i18n/messages/*.json)
export const TITLE_TOGGLE = 'Show translation fields'
export const TIP_TOGGLE = 'Shows the stored translations of multilingual fields next to them: rows and columns «… (uk)», «… (ru)». Only for you, remembered in this browser.'
export const TIP_AUTO = 'Dictionary translation, not saved in the record'
export const TIP_EN = 'English base value. To change it, switch the interface to English or use Translations'

// binding-и, которые карточка Translations признаёт и без флага translate (AttrGrid.i18n_targetFor)
const DEFAULT_TARGETS = ['title', 'tooltip', 'comment', 'doc.tooltip']
const TEXT_TYPES = ['AttrStr', 'AttrStrMulti']
const SERVICE_RE = /(^|\.)_/            // служебные пути: _… / …._… (как store.is_serviceBinding)
const AUTO_KEY_RE = /^title_[a-z]{2}$/  // снимаются только словарные названия (бэк views.py снимает те же title_<L>)

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const langId = (L) => (L && typeof L === 'object') ? L.id : L

// значение по пути 'a.b.c' (без store.get_value: помощник чистый)
export function getPath(obj, path) {
    let v = obj
    for (const part of String(path || '').split('.')) {
        if (v === null || v === undefined || typeof v !== 'object') return undefined
        v = v[part]
    }
    return v
}

// ---- путь перевода ----
// правило store.localize_binding (mainStore делегирует сюда): у названия ЗАПИСИ ('title') и строк-записей реестров (_doc.*) — meta.title_<L>;
// title строки конфигурации (doc.viewList.3.title …) и все прочие поля — суффикс рядом с ключом
export function localizeBinding(binding, lang, raw_binding = binding) {
    if (raw_binding === 'title') {
        if (binding !== 'title' && !String(binding).startsWith('_')) return binding + '_' + lang
        return binding.slice(0, binding.length - 'title'.length) + 'meta.title_' + lang
    }
    return binding + '_' + lang
}
// путь перевода поля на языке L относительно записи-владельца (DESIGN §1.3). opts: { nested — поле СТРОКИ записи (форма widget-row:
// store.i18n_linkBinding(...).nested) → путь ОТНОСИТЕЛЬНО строки (путь родителя допишет attr_get); rowPath — путь этой строки в записи-владельце
// (rowPathOf): правило localize применяется к полному пути строки, как у attr_get основного поля (get_value_localized) — строка-запись реестра
// (_doc.data.3) title → meta.title_<L>, строка конфигурации (doc.viewList.3) → title_<L>; без rowPath — '<binding>_<L>';
// localize — своё правило вместо localizeBinding (store.localize_binding, AttrGrid.i18n_rowLangBinding); raw — исходный binding колонки, если binding уже с префиксом }
export function fieldPath(binding, lang, opts = {}) {
    const b = typeof binding === 'string' ? binding : ''
    const L = langId(lang)
    if (!b || !L) return ''
    const loc = opts && typeof opts.localize === 'function' ? opts.localize : localizeBinding
    if (opts && opts.nested) {
        // IE 2026 10 06 / ODOO-341407 (ревью U9: карточка Alt+F2 строки списка писала '_doc.data.N.title_uk', а список и карточка читают
        // '_doc.data.N.meta.title_uk'): путь — правилом от полного пути строки, минус путь строки (требование ie-dm7: путь выводится из binding)
        const rp = typeof opts.rowPath === 'string' ? opts.rowPath : ''
        if (rp) {
            const p = loc(rp + '.' + b, L, (opts && opts.raw) || b)
            if (typeof p === 'string' && p.startsWith(rp + '.')) return p.slice(rp.length + 1)
        }
        return b + '_' + L
    }
    return loc(b, L, (opts && opts.raw) || b)
}
// IE 2026 10 06 / ODOO-341407: путь строки записи-владельца, которой принадлежит поле формы widget-row; link — store.i18n_linkBinding(W, binding)
// ('_doc.data.3.title' → '_doc.data.3', 'doc.viewList.2.title' → 'doc.viewList.2'); поле самой записи (не nested) — ''
export function rowPathOf(link, binding) {
    const full = link && typeof link.binding === 'string' ? link.binding : ''
    if (!link || !link.nested || typeof binding !== 'string' || !binding || !full.endsWith('.' + binding)) return ''
    return full.slice(0, full.length - binding.length - 1)
}
// IE 2026 10 06 / ODOO-341407 (проверка round 2, C6.row-rows / C6.row-card-apply): строка-ЗАПИСЬ виджета-списка под формой Alt+F2 (widget-row над
// _doc.data.N): link — store.i18n_linkBinding(W, '') (или с binding = путь строки). Объект строки | null. Список записей — WIDGET-LIST, сегмент
// данных не витрина DS (как AttrGrid.i18nFields_isListGrid); путь — строка таблицы данных ('_doc.data.3', у дерева — '_doc.data.3.attrs.2');
// у строки есть сегмент (строки DS / расчётные — без него). Форма такой строки рисует колонки списка (formatRowsAsColumns), это поля записи строки:
// их переводы — у самой строки (title → meta.title_<L>, doc.x → doc.x_<L>), записываются в запись строки, не в запись виджета-списка
const LIST_ROW_RE = /^_doc\.data\.\d+(\.[A-Za-z][\w]*\.\d+)*$/
export function listRowOf(link) {
    if (!link || !link.nested || !isObj(link.record)) return null
    const rec = link.record
    if (String(rec.segment || '') !== 'WIDGET-LIST') return null
    if (String(getPath(rec, 'doc.params.segment') || '').startsWith('DATA-SOURCE')) return null
    const rp = typeof link.binding === 'string' ? link.binding : ''
    if (!LIST_ROW_RE.test(rp)) return null
    const row = getPath(rec, rp)
    return isObj(row) && row.segment ? row : null
}
// контейнер перевода в строке — первый сегмент пути перевода ('title' → 'meta', 'doc.legacy' → 'doc', 'meta.comment' → 'meta');
// '' — перевод лёг бы ключом верхнего уровня строки (колонкой таблицы) — такую колонку списка не строим
export function fieldBox(binding, opts = {}) {
    const p = fieldPath(binding, 'xx', opts)
    const i = p.indexOf('.')
    return i > 0 ? p.slice(0, i) : ''
}
// у строки есть объект-контейнер перевода (строки DS / noExtraFields / aggregateSelect приходят без meta / doc — править их нельзя: update_data
// бэка заменил бы meta записи целиком, R3 §4.5)
export function rowHasBox(row, binding, opts = {}) {
    const k = fieldBox(binding, opts)
    return !!k && isObj(row) && isObj(row[k])
}

// ---- какие поля переводимые (DESIGN §1.5) ----
export function isTranslatable(attr) {
    if (!attr || typeof attr !== 'object' || attr._i18nOf) return false   // виртуальная строка / колонка сама не переводится
    const b = attr.binding
    if (typeof b !== 'string' || !b || SERVICE_RE.test(b)) return false
    if (attr.attr_type && !TEXT_TYPES.includes(attr.attr_type)) return false
    return !!attr.translate || DEFAULT_TARGETS.includes(b)
}

// языки полей перевода: включённые в базе (enabledLanguages(), en первым) без английской базы; withEn — en первым
export function fieldLangs(enabled, opts = {}) {
    const list = Array.isArray(enabled) ? enabled.filter(l => l && l.id) : []
    const other = list.filter(l => l.id !== 'en')
    if (!opts || !opts.withEn) return other
    return [list.find(l => l.id === 'en') || { id: 'en', title: 'English' }, ...other]
}

// binding-и узлов вида (рекурсивно по attrs) — дедупликация с явными полями «Title (uk)/(ru)» (SOLUTION / SOLUTION-RULE, решение Игоря В4)
export function bindingsOf(attrs, childKey = 'attrs', out = new Set(), depth = 0) {
    if (!Array.isArray(attrs) || depth > 20) return out
    for (const a of attrs) {
        if (!a || typeof a !== 'object') continue
        if (typeof a.binding === 'string' && a.binding) out.add(a.binding)
        if (Array.isArray(a[childKey])) bindingsOf(a[childKey], childKey, out, depth + 1)
    }
    return out
}

// ---- словарный перевод предопределённой записи (meta._i18n_auto) ----
// ключ meta по пути: 'meta.title_uk' | 'title_uk' → 'title_uk'; прочие пути ('doc.comment_uk', 'doc.viewList.3.title_uk') — ''
function autoKeyOf(path) {
    let k = String(path || '')
    if (k.startsWith('meta.')) k = k.slice(5)
    return (k && !k.includes('.')) ? k : ''
}
// значение по пути — словарный перевод, ещё не сохранённый в записи (серый показ, подсказка TIP_AUTO)
export function isAuto(record, path) {
    const k = autoKeyOf(path), auto = isObj(record) && isObj(record.meta) ? record.meta[AUTO_MARK] : null
    return !!k && Array.isArray(auto) && auto.includes(k)
}
// правка ключа снимает его из пометки: значение стало ручным (иначе сохранение списка сняло бы его как словарное). Пустая пометка удаляется.
// true — пометка была и снята
export function autoUnmark(record, path) {
    const k = autoKeyOf(path)
    const meta = isObj(record) ? record.meta : null
    if (!k || !isObj(meta)) return false
    const auto = meta[AUTO_MARK]
    if (!Array.isArray(auto) || !auto.includes(k)) return false
    const rest = auto.filter(x => x !== k)
    if (rest.length) meta[AUTO_MARK] = rest
    else delete meta[AUTO_MARK]
    return true
}
// IE 2026 10 06 / ODOO-341407 (ревью U9): то же по пути от записи-владельца — пометка лежит у объекта, которому принадлежит этот meta
// ('_doc.data.3.meta.title_uk' → строка _doc.data.3 списка под карточкой Alt+F2; 'meta.title_uk' / 'title_uk' — сама запись, как autoUnmark)
export function autoUnmarkAt(record, path) {
    const p = String(path || ''), i = p.lastIndexOf('meta.')
    if (i <= 0 || p[i - 1] !== '.') return autoUnmark(record, p)
    return autoUnmark(getPath(record, p.slice(0, i - 1)), p.slice(i))
}
// IE 2026 10 06 / ODOO-341407 (ревью U9: новая запись под uk получала базовое название «37»): правка основной translate-строки под uk / ru
// пишет БАЗУ, как раньше, у записи, которой ещё нет в базе — бэк saveRecord2 уводит правку базы в перевод только у записи с id (views.py:
// `if record.get('id')` … recordDB), новую сохраняет как есть, и пустое название стало бы номером (set_initial_values), а введённый текст —
// только переводом. Копия (_meta.clone: база — название источника с номером) — перевод, пока база поля непустая. basePath — путь базы в записи
export function newRecordBaseEdit(record, basePath) {
    if (!isObj(record) || record.id) return false
    if (!(isObj(record._meta) && record._meta.clone)) return true
    const v = getPath(record, basePath)
    return v === undefined || v === null || v === ''
}
// копия строки для сохранения списка (widget_LIST.save → update_data заменяет meta записи целиком, BE attrs.py:1771-1772): без словарных
// meta.title_<L> из пометки и без самой пометки. Строка и её meta в памяти не меняются; пометки нет — та же строка (без копии)
export function stripAutoForSave(row) {
    const meta = isObj(row) ? row.meta : null
    if (!isObj(meta) || !(AUTO_MARK in meta)) return row
    const m = { ...meta }
    const auto = Array.isArray(m[AUTO_MARK]) ? m[AUTO_MARK] : []
    for (const k of auto) if (typeof k === 'string' && AUTO_KEY_RE.test(k)) delete m[k]
    delete m[AUTO_MARK]
    return { ...row, meta: m }
}

// ---- виртуальные строки формы (AttrTabGrid: форма записи, вкладка «Інфо», форма строки widget-row; DESIGN §3.2) ----
// attr — узел вида (основная строка поля). ctx: {
//   langs     — i18n_fieldLangs() (без en): строки «(uk)», «(ru)» в порядке базы;
//   withEn    — интерфейс не на en: перед ними строка «(en)» с английской базой, только чтение (решение Игоря В2; у nested не строится);
//   nested    — поле строки записи (widget-row): путь относительно строки;
//   rowPath   — путь этой строки в записи-владельце (rowPathOf / i18n_linkBinding(W, '').binding): строка-запись реестра (_doc.data.3) — title →
//               meta.title_<L> и словарный перевод по её meta._i18n_auto, строка конфигурации — '<binding>_<L>' (IE 2026 10 06 / ODOO-341407);
//   readOnly  — WIDGET.isReadOnly || store.readonly_on();
//   record    — запись-владелец (link.record): (en) названия — meta.title_en, если непустой; словарные meta.title_<L> (пометка _i18n_auto);
//   existing  — Set binding-ов вида (bindingsOf): строку с путём, который уже есть в виде, не добавляем;
//   localize  — правило пути (binding, lang, raw) => path, по умолчанию localizeBinding;
//   row       — строка-запись списка под формой Alt+F2 (listRowOf): у поля без контейнера перевода в строке (meta / doc) строк нет — правка
//               завела бы контейнер, и update_data бэка заменил бы meta записи (как колонки списка, rowHasBox; IE 2026 10 06 / ODOO-341407) }
// Правка основной translate-строки под uk / ru (решение В3, FLAG_FORM_EDIT) пишет в fieldPath(attr.binding, lang, { nested, rowPath, raw: attr.binding }).
// Возвращает узлы V (dataItem.attr). V._i18nOf (неперечисляемое — в данные и в вид не попадает): { binding, lang, kind: 'form', auto, en }.
// auto — значение словарное (AttrGrid красит строку 'i18n-auto'), en — строка английской базы.
export function formRowsFor(attr, ctx = {}) {
    if (!isTranslatable(attr)) return []
    const b = attr.binding, nested = !!ctx.nested, existing = ctx.existing instanceof Set ? ctx.existing : null
    const attr_type = attr.attr_type === 'AttrStrMulti' ? 'AttrStrMulti' : 'AttrStr'
    const label = attr.title || b
    const ro = !!(attr.isReadOnly || ctx.readOnly)
    const rowPath = nested && typeof ctx.rowPath === 'string' ? ctx.rowPath : ''   // IE 2026 10 06 / ODOO-341407 (ревью U9): путь строки — правило от её полного пути
    const opts = { nested, rowPath, localize: ctx.localize }
    if (ctx.row && !rowHasBox(ctx.row, b, opts)) return []   // IE 2026 10 06 / ODOO-341407: строка-запись списка без контейнера перевода поля
    const autoRec =nested ? (rowPath ? getPath(ctx.record, rowPath) : null) : ctx.record   // IE 2026 10 06 / ODOO-341407: словарный перевод строки-записи — по её meta
    const make = (binding, lang, extra, of) => {
        const V = { attr_type, binding, title: label + ' (' + lang + ')', translate: false, isReadOnly: ro, showEmpty: true, attrs: [], ...extra }
        // IE 2026 10 06 / ODOO-341407 (U9 D1): writable / configurable — строки живут в реактивном состоянии грида (this.data); get-ловушка прокси Vue
        // отдаёт reactive(значение), и у неизменяемого ненастраиваемого свойства это нарушало инвариант прокси (TypeError на каждом чтении _i18nOf)
        Object.defineProperty(V, '_i18nOf', { value: { binding: b, lang, kind: 'form', auto: false, en: false, ...of }, enumerable: false, writable: true, configurable: true })
        return V
    }
    const out = []
    if (ctx.withEn && !nested) {
        // английская база: у названия записи с не английской базой — meta.title_en (поле 1С / CLDR / ручной), иначе сам binding (translate: false —
        // attr_get читает сырой ключ, без перевода). Только чтение: Save под uk / ru уводит правку базы в перевод (BE views.py:4458-4463)
        const pEn = fieldPath(b, 'en', opts)
        const v = b === 'title' ? getPath(ctx.record, pEn) : ''
        const enBinding = (typeof v === 'string' && v) ? pEn : b
        if (enBinding === b || !existing || !existing.has(enBinding)) out.push(make(enBinding, 'en', { isReadOnly: true, tooltip: t(TIP_EN) }, { en: true }))
    }
    for (const L of ctx.langs || []) {
        const id = langId(L)
        if (!id || id === 'en') continue
        const p = fieldPath(b, id, opts)
        if (!p || (existing && existing.has(p))) continue
        const auto = isAuto(autoRec, p)   // IE 2026 10 06 / ODOO-341407: было !nested && isAuto(ctx.record, p)
        out.push(make(p, id, auto ? { tooltip: t(TIP_AUTO) } : {}, { auto }))
    }
    return out
}

// ---- виртуальные колонки виджета-списка (таблица данных _doc.data; DESIGN §3.4) ----
// cols — колонки грида (this.columns(), после collapse_arrowLayer_attrs). ctx: {
//   langs     — i18n_fieldLangs() (без en; колонки (en) в списке нет: binding title занят базовой колонкой);
//   rows      — загруженные строки: колонки только если хоть у одной строки есть контейнер перевода (rowHasBox);
//   isHidden  — (col) => bool: колонка-основа скрыта — копий нет;
//   readOnly  — WIDGET.isReadOnly || store.readonly_on();
//   childPath — attr.column_childItemsPath: колонки групп (копия группы с новым массивом детей, сами колонки не копируются);
//   localize  — правило пути (binding, lang, raw) => path, сигнатура store.localize_binding; по умолчанию localizeBinding — для таблицы _doc.data
//               это и есть AttrGrid.i18n_rowLangBinding (у него порядок аргументов другой: (col_binding, raw_binding, lang)) }
// Чтение опции (store.i18nFields_on) и языков — один раз на сборку колонок / строк (computed), не на ячейку: они читают настройки базы.
// Возвращает НОВЫЙ массив (исходный attr.attrs не мутируется: узел вида уходит на бэк и в ⚙ таблицы) или тот же, если добавлять нечего.
// Колонка-копия — только нужные поля (без спреда основы: translate, бэкап локализации _i18n / title_en, conditional_format, relation_segment,
// v-if не переезжают); title — геттер (живая смена языка: подпись основы перелокализуется); _i18nOf — неперечисляемое { binding, lang, kind: 'list' }.
export function listColsFor(cols, ctx = {}) {
    if (!Array.isArray(cols) || !cols.length) return cols
    const langs = (ctx.langs || []).map(langId).filter(id => id && id !== 'en')
    if (!langs.length) return cols
    const childPath = ctx.childPath || ''
    // занятые пути: свой Set на весь обход (и групп) — Set вызывающего (ctx.existing) не мутируем
    const existing = ctx._seen || new Set(ctx.existing instanceof Set ? ctx.existing : bindingsOf(cols, childPath || 'attrs'))
    const rows = Array.isArray(ctx.rows) ? ctx.rows : []
    const boxSeen = {}
    const hasBox = (k) => (k in boxSeen) ? boxSeen[k] : (boxSeen[k] = rows.some(r => isObj(r) && isObj(r[k])))
    const opts = { localize: ctx.localize }
    let changed = false
    const out = []
    for (const c of cols) {
        // группа — как у сканирования колонок грида (scan_columnGroups_fromAttrs): непустые дети и не AttrGrid; лист с пустым attrs: [] — колонка
        const kids = childPath && c && Array.isArray(c[childPath]) && c[childPath].length && c.attr_type !== 'AttrGrid' ? c[childPath] : null   // IE 2026 10 06 / ODOO-341407 (U6): было — любой массив (лист с attrs: [] пропускался)
        if (kids) {
            const nk = listColsFor(kids, { ...ctx, _seen: existing })
            if (nk !== kids) { out.push({ ...c, [childPath]: nk }); changed = true } else out.push(c)
            continue
        }
        out.push(c)
        if (!c || typeof c !== 'object' || c._i18nOf || !c.translate) continue
        if (c.attr_type && !TEXT_TYPES.includes(c.attr_type)) continue   // IE 2026 10 06 / ODOO-341407 (U6): только текст — как isTranslatable у строк формы (число / ссылка с translate — без копий)
        if (typeof c.binding !== 'string' || !c.binding || SERVICE_RE.test(c.binding)) continue
        if (typeof ctx.isHidden === 'function' && ctx.isHidden(c)) continue
        const box = fieldBox(c.binding, opts)
        if (!box || !hasBox(box)) continue
        for (const L of langs) {
            const binding = fieldPath(c.binding, L, opts)
            if (!binding || existing.has(binding)) continue
            existing.add(binding)
            out.push(listCol(c, L, binding, ctx))
            changed = true
        }
    }
    return changed ? out : cols
}
function listCol(c, L, binding, ctx) {
    const vc = { binding, attr_type: c.attr_type === 'AttrStrMulti' ? 'AttrStrMulti' : 'AttrStr',
        width: c.width, minWidth: c.minWidth, isReadOnly: !!c.isReadOnly || !!ctx.readOnly, showEmpty: true }
    // сеттер — no-op: код грида, пишущий подписи колонок, не падает на свойстве-геттере (модуль в strict mode)
    Object.defineProperty(vc, 'title', { get: () => (c.title || c.binding) + ' (' + L + ')', set: () => {}, enumerable: true, configurable: true })
    // IE 2026 10 06 / ODOO-341407 (U9 D1): writable / configurable — колонка живёт в реактивном attr_by_binding грида (инвариант прокси Vue, см. formRowsFor)
    Object.defineProperty(vc, '_i18nOf', { value: { binding: c.binding, lang: L, kind: 'list' }, enumerable: false, writable: true, configurable: true })
    return vc
}

// ---- IE 2026 10 06 / ODOO-341407 (DESIGN §3.3, U8): отдельное translate-поле формы — AttrStrMulti (Comment вкладки «Інфо»: BE F_comment binding
// meta.comment стоит ПОСЛЕ грида, строкой AttrTabGrid его не дополнить — поля переводов рисует сам компонент) ----
// поля переводов под полем attr виджета W: узлы formRowsFor (контракт DESIGN §3.2) — binding — путь перевода ОТНОСИТЕЛЬНО формы W, выведенный из
// binding поля (meta.comment → meta.comment_uk, doc.x → doc.x_uk, title → meta.title_uk; у формы строки widget-row — '<binding>_<L>', путь
// записи-владельца допишут store.attr_get / attr_set); под uk / ru первой идёт «(en)» — английская база, только чтение (решение Игоря В2).
// [] — опция «Show translation fields» у виджета выключена / недоступна, поле не переводимое, выпадающий редактор (attr.dropdown; opts.initial —
// компонент над чужим узлом). opts.existing — Set binding-ов соседних узлов вида: путь, который уже есть в виде, не дублируется
export function companionRowsFor(store, W, attr, opts = {}) {
    if (!store || !W || typeof W !== 'object' || !isTranslatable(attr) || attr.dropdown || (opts && opts.initial)) return []
    let on = false
    try { on = !!store.i18nFields_on(W) } catch (e) { on = false }
    if (!on) return []
    const link = store.i18n_linkBinding(W, attr.binding) || {}
    if (!isObj(link.record)) return []
    let lang = '', ro = !!W.isReadOnly
    try { lang = store.get_lang() || '' } catch (e) { lang = '' }
    try { ro = ro || !!store.readonly_on() } catch (e) { /* стор без роли «только чтение» */ }
    return formRowsFor(attr, { langs: store.i18n_fieldLangs(), withEn: !!lang && lang !== 'en', nested: !!link.nested, readOnly: ro, record: link.record,
        rowPath: rowPathOf(link, attr.binding),   // IE 2026 10 06 / ODOO-341407 (ревью U9): строка-запись реестра — meta.title_<L>, как читает attr_get
        existing: opts && opts.existing instanceof Set ? opts.existing : null })
}
// правка САМОГО translate-поля формы под uk / ru (решение Игоря В3, выключатель fe.i18n.formEditTranslation, по умолчанию вкл.): пишется только
// перевод текущего языка — путь из binding поля, как у companionRowsFor; английская база не трогается (как F2 грида и F1 шапки). Без этого правка
// под uk ложилась в базу: meta.comment «Інфо» нет в fields_map сегмента, saveRecord2 бэка её в перевод не уводит (R2 §6), а у полей из
// fields_map бэк кладёт правку базы в перевод поверх правки поля «(uk)».
// value — новый текст; показанный сейчас — store.attr_get(W, attr) (перевод, если непустой, иначе база).
// null — писать как раньше (store.attr_set(W, attr, value)): интерфейс en, выключатель, поле не translate / служебное / размножается на диапазон
// ячеек (raw_binding), форма строки чипса / параметров вида, значение не строка, запись ещё не в базе (newRecordBaseEdit — IE 2026 10 06 / ODOO-341407).
// Иначе { binding — путь перевода относительно W (для store.attr_set), full — тот же путь от записи-владельца (снятие пометки словарного перевода:
// store.i18n_autoUnmark), record — запись-владелец, nested, lang, changed } — changed: false, если
// текст равен показанному: перечитывание поля из записи (editor.setValue в AttrStrMulti) тоже даёт событие правки — это не правка, перевод не создаём
export function formEditTarget(store, W, attr, value) {
    if (!store || !W || typeof W !== 'object' || !attr || !attr.translate || typeof value !== 'string' || attr.raw_binding) return null
    const b = attr.binding
    if (typeof b !== 'string' || !b || SERVICE_RE.test(b)) return null
    let lang = ''
    try { lang = store.get_lang() || '' } catch (e) { lang = '' }
    if (!lang || lang === 'en') return null
    try { if (store.setting(FLAG_FORM_EDIT, true) === false) return null } catch (e) { /* без настроек — вкл. */ }
    const type = isObj(W.record) && isObj(W.record._meta) ? W.record._meta.type : ''
    if (type === 'widget-row-chip' || type === 'widget-row-viewParams') return null
    const link = store.i18n_linkBinding(W, b) || {}
    if (!isObj(link.record)) return null
    if (newRecordBaseEdit(link.record, link.binding || b)) return null   // IE 2026 10 06 / ODOO-341407 (ревью U9): новая запись — правка в базу, как раньше
    const nested = !!link.nested
    const rowPath = rowPathOf(link, b)   // IE 2026 10 06 / ODOO-341407 (ревью U9): строка-запись реестра под карточкой Alt+F2 — meta.title_<L>
    const binding = fieldPath(b, lang, { nested, rowPath })
    if (!binding) return null
    let shown = ''
    try { shown = store.attr_get(W, attr) } catch (e) { shown = '' }
    const full = !nested ? binding : (rowPath ? rowPath + '.' + binding : '')
    return { binding, full, record: link.record, nested, lang, changed: value !== String(shown || '') }
}

// ---- личная опция «Show translation fields» (стор: store.i18nFields_kind / _avail / _on / _set — обёртки над этими функциями) ----
// сегмент данных виджета: форма записи — record.segment (SOLUTION-RULE); список — record.doc.params.segment (тот же SOLUTION-RULE, BE widgets.py);
// форма строки widget-row — сегмент виджета-владельца (store.i18n_linkBinding); прочие — record.segment (WIDGET-REPORT2, DATA-SOURCE…)
export function i18nFieldsKind(store, W, depth = 0) {
    if (!W || typeof W !== 'object' || depth > 5) return ''
    if (W.widget === 'widget-row') {
        let owner = null
        try { owner = store.i18n_linkBinding(W, '').WIDGET } catch (e) { owner = null }
        return owner && owner !== W ? i18nFieldsKind(store, owner, depth + 1) : ''
    }
    const rec = W.record
    if (!isObj(rec)) return ''
    if (W.widget_class === 'widget_LIST' || W.widget === 'widget-list') {
        const s = isObj(rec.doc) && isObj(rec.doc.params) ? rec.doc.params.segment : ''
        if (s) return String(s)
    }
    return String(rec.segment || '')
}
// пункт ≡ виджета есть: механизм не выключен, в базе включено больше одного языка, у записи (владельца) есть сегмент, виджет не поиск;
// формы строки чипса / параметров вида (widget-row-chip / widget-row-viewParams) — переводимых полей записи там нет.
// IE 2026 10 06 / ODOO-341407 (U9 D2): свёрнутость (minMode) не учитывается — меню свёрнутого виджета строится один раз и после разворота
// оставалось без пункта, а опция «выключалась» на сворачивании и пересобирала гриды (i18nFieldsOn читает доступность)
export function i18nFieldsAvail(store, W) {
    if (!store || !W || typeof W !== 'object') return false
    try { if (store.setting(FLAG_FIELDS, true) === false) return false } catch (e) { /* без настроек — вкл. */ }
    if (W.widget === 'widget-search') return false   // IE 2026 10 06 / ODOO-341407 (U9 D2): было || styleWidget.minMode
    const type = W.record && W.record._meta ? W.record._meta.type : ''
    if (type === 'widget-row-chip' || type === 'widget-row-viewParams') return false
    let langs = []
    try { langs = store.i18n_fieldLangs() || [] } catch (e) { langs = [] }
    if (!langs.length) return false
    return !!i18nFieldsKind(store, W)
}
export function i18nFieldsStorageKey(store) {
    const u = (store && store.user && (store.user.id || store.user.email)) || 'user'
    const db = (store && store.user && store.user.database && store.user.database.dbid) || ''
    return `${STORAGE_PREFIX}:${u}:${db}`
}
function storageRead(key) {
    const map = {}
    try {
        const v = JSON.parse(localStorage.getItem(key) || 'null')
        if (isObj(v)) for (const [k, on] of Object.entries(v)) if (on === true) map[k] = true
    } catch (e) { /* хранилище недоступно / чужой формат — пусто (опция по умолчанию выкл.) */ }
    return map
}
function storageWrite(key, map) {
    try {
        if (Object.keys(map).length) localStorage.setItem(key, JSON.stringify(map))
        else localStorage.removeItem(key)
    } catch (e) { /* хранилище недоступно — состояние живёт в памяти стора до перезагрузки */ }
}
// рабочее состояние стора для текущего пользователя и базы; другой ключ (вход, смена базы) — карта поднимается из localStorage.
// Пишет в стор — звать только из обработчиков (i18nFieldsSet), не из computed
function stateEnsure(store) {
    let st = store.i18nFields
    if (!isObj(st)) st = store.i18nFields = { key: '', map: {} }
    const key = i18nFieldsStorageKey(store)
    if (st.key !== key) { st.map = storageRead(key); st.key = key }
    else if (!isObj(st.map)) st.map = {}
    return st
}
// включена ли опция у виджета (реактивно: читается карта стора — computed гридов пересчитываются по переключению). Пока карта текущего ключа
// не поднята (переключений в этой вкладке ещё не было) — значение из localStorage напрямую, стор при чтении не меняется
export function i18nFieldsOn(store, W) {
    if (!i18nFieldsAvail(store, W)) return false
    const kind = i18nFieldsKind(store, W)
    const st = store.i18nFields, key = i18nFieldsStorageKey(store)
    if (isObj(st) && st.key === key && isObj(st.map)) return st.map[kind] === true
    return storageRead(key)[kind] === true
}
// переключить опцию: только память стора и localStorage — ни записи виджета, ни её _meta.modified, ни doc.viewParams (DESIGN §1.4, R5 §3).
// Возвращает новое состояние (false — у виджета нет сегмента данных)
export function i18nFieldsSet(store, W, on) {
    const kind = i18nFieldsKind(store, W)
    if (!kind) return false
    const st = stateEnsure(store)
    if (on) st.map[kind] = true
    else delete st.map[kind]
    storageWrite(st.key, st.map)
    return !!on
}
